import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";
import { saleService } from "@/services/sale";
import type { SaleListParams } from "@/services/sale";

import type { CreateSaleReq, UpdateSaleReq } from "@artisancode/api-types";

export function useSales(params?: SaleListParams) {
  return useQuery({
    queryKey: queryKeys.sales.list(params as Record<string, unknown>),
    queryFn: () => saleService.list(params),
  });
}

export function useCreateSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      body,
      idempotencyKey,
    }: {
      body: CreateSaleReq;
      idempotencyKey?: string;
    }) => saleService.create(body, idempotencyKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.sales.all });
      qc.invalidateQueries({ queryKey: queryKeys.stocks.all });
    },
  });
}

export function useUpdateSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & UpdateSaleReq) =>
      saleService.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.sales.all });
      qc.invalidateQueries({ queryKey: queryKeys.stocks.all });
    },
  });
}

export function useDeleteSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      saleService.delete(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.sales.all });
      qc.invalidateQueries({ queryKey: queryKeys.stocks.all });
    },
  });
}

export function useVoidSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      saleService.void(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.sales.all });
      qc.invalidateQueries({ queryKey: queryKeys.stocks.all });
    },
  });
}
